import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Camera } from 'lucide-react';
import api from '../utils/api';
import { LT } from '../lib/appCatalogue';
import { useLocale, useT } from '../contexts/LocaleProvider';

/* MANEVA polaroid · the rotating photo widget that lives on both
 * the newsletters landing page and the Splash homepage. Reads
 * exclusively from /api/newsletter-photos/ so the admin CRUD at
 * /admin/newsletter-photos and the carousel always agree. HR
 * uploads land instantly on both surfaces.
 *
 * Visual: a small stack of polaroid cards, top one is active, two
 * fanned out behind. Auto-advances every 5 s; clicking the deck or
 * the dots cycles forward. Caption + episode link sit underneath.
 *
 * `mode`        — 'full' (newsletters home) or 'compact' (splash block).
 * `episodeSlug` — if set, only photos tagged with this slug are shown.
 * `splashOnly`  — if true, only photos with show_on_splash=true.
 */
export default function NewsletterPolaroid({
  mode = 'full',
  episodeSlug = null,
  splashOnly = false,
}) {
  const t = useT();
  const { intlLocale } = useLocale();
  const [photos, setPhotos] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const params = {};
    if (episodeSlug) params.episode = episodeSlug;
    if (splashOnly) params.splash = 1;
    api.newsletterPhotos(params)
      .then(rows => {
        if (cancelled) return;
        setPhotos(Array.isArray(rows) ? rows : (rows?.results || []));
        setLoaded(true);
      })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [episodeSlug, splashOnly]);

  const live = photos;

  useEffect(() => {
    if (live.length < 2) return undefined;
    timerRef.current = setInterval(() => {
      setIdx(i => (i + 1) % live.length);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [live.length]);

  const advance = () => {
    if (live.length < 2) return;
    setIdx(i => (i + 1) % live.length);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setIdx(i => (i + 1) % live.length);
      }, 5000);
    }
  };

  /* sizing tokens — keep dimensions in one place so 'compact' and
     'full' look like the same widget at different scales. */
  const isFull = mode === 'full';
  const cardW = isFull ? 280 : 200;
  const photoH = isFull ? 220 : 156;
  const padTop = isFull ? 14 : 10;
  const padBottom = isFull ? 56 : 38;

  if (!loaded) {
    return <Skeleton w={cardW} h={photoH + padTop + padBottom} />;
  }

  if (live.length === 0) {
    return <EmptyState w={cardW} h={photoH + padTop + padBottom} isFull={isFull} t={t} />;
  }

  const active = live[idx % live.length];
  const nextA = live[(idx + 1) % live.length];
  const nextB = live[(idx + 2) % live.length];

  return (
    <div className="flex flex-col items-center select-none">
      <div className="relative cursor-pointer"
           style={{ width: cardW + 40, height: photoH + padTop + padBottom + 30 }}
           onClick={advance}
           role="button"
           aria-label={t('polaroid.next')}>
        {/* back card */}
        {live.length > 2 && (
          <PolaroidCard photo={nextB} w={cardW} photoH={photoH} padTop={padTop} padBottom={padBottom}
                        style={{ transform: 'translate(-50%, 0) rotate(-6deg)', left: '50%', top: 16, zIndex: 1, opacity: 0.85 }} />
        )}
        {/* mid card */}
        {live.length > 1 && (
          <PolaroidCard photo={nextA} w={cardW} photoH={photoH} padTop={padTop} padBottom={padBottom}
                        style={{ transform: 'translate(-50%, 0) rotate(3deg)', left: '50%', top: 8, zIndex: 2, opacity: 0.95 }} />
        )}
        {/* top (active) */}
        <PolaroidCard photo={active} w={cardW} photoH={photoH} padTop={padTop} padBottom={padBottom}
                      style={{ transform: 'translate(-50%, 0) rotate(-1.2deg)', left: '50%', top: 0, zIndex: 3 }}
                      showCaption intlLocale={intlLocale} />
      </div>

      {/* footer · dots + link out */}
      <div className="mt-2 flex items-center gap-2">
        {live.slice(0, Math.min(live.length, 6)).map((_, i) => (
          <button key={i}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: i === idx ? 16 : 6,
                    background: i === idx ? LT.greenDeep : LT.green30,
                  }}
                  aria-label={`Photo ${i + 1}`} />
        ))}
        {live.length > 6 && (
          <span className="text-[10px] font-num" style={{ color: LT.green50 }}>
            +{live.length - 6}
          </span>
        )}
      </div>

      {isFull && (
        <Link to="/resources/newsletters"
              className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium"
              style={{ color: LT.greenDeep }}>
          <Camera className="h-3.5 w-3.5" /> {t('polaroid.albumLink')}
        </Link>
      )}
    </div>
  );
}

const PolaroidCard = ({ photo, w, photoH, padTop, padBottom, style, showCaption, intlLocale }) => (
  <div className="absolute flex flex-col"
       style={{
         width: w,
         background: '#fefefe',
         borderRadius: 4,
         padding: `${padTop}px ${padTop}px 0`,
         boxShadow: '0 10px 24px rgba(43,65,56,0.22), 0 2px 6px rgba(43,65,56,0.1)',
         transformOrigin: 'top center',
         transition: 'transform 320ms ease, opacity 320ms ease',
         ...style,
       }}>
    <div style={{
      width: '100%',
      height: photoH,
      background: '#1c2a23',
      borderRadius: 2,
      overflow: 'hidden',
    }}>
      <img src={photo.image} alt={photo.caption || ''}
           className="w-full h-full object-cover"
           draggable={false} />
    </div>
    {/* Caption sits naturally below the photo in the white bottom
        margin. Sized to match the polaroid's bottom padding so the
        whole card stays the same height whether the top card shows
        a caption or not. */}
    <div className="flex flex-col items-center justify-center text-center px-3"
         style={{ height: padBottom, gap: 4 }}>
      {showCaption ? (
        <>
          <div className="font-display leading-tight tracking-tight"
               style={{
                 fontSize: '0.92rem',
                 color: LT.greenDeep,
                 fontStyle: 'italic',
                 maxWidth: '100%',
                 overflow: 'hidden',
                 textOverflow: 'ellipsis',
                 whiteSpace: 'nowrap',
               }}>
            {photo.caption || ''}
          </div>
          {photo.taken_at && (
            <div className="text-[9.5px] tracking-[0.14em] uppercase font-num"
                 style={{ color: LT.green50 }}>
              {formatDate(photo.taken_at, intlLocale)}
            </div>
          )}
        </>
      ) : null}
    </div>
  </div>
);

const Skeleton = ({ w, h }) => (
  <div className="relative animate-pulse"
       style={{
         width: w + 40, height: h + 30,
       }}>
    <div className="absolute"
         style={{
           left: '50%', top: 0,
           transform: 'translate(-50%, 0) rotate(-1.2deg)',
           width: w, height: h,
           background: '#f3f3f0', borderRadius: 4,
           boxShadow: '0 10px 24px rgba(43,65,56,0.12)',
         }} />
  </div>
);

const EmptyState = ({ w, h, isFull, t }) => (
  <div className="flex flex-col items-center text-center" style={{ width: w + 40 }}>
    <div className="relative"
         style={{
           width: w, height: h,
           background: '#fefefe', borderRadius: 4,
           transform: 'rotate(-1.2deg)',
           boxShadow: '0 10px 24px rgba(43,65,56,0.18)',
           padding: 14,
         }}>
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 rounded-sm"
           style={{ background: LT.beigeLight, color: LT.green50 }}>
        <Camera className="h-7 w-7" />
        <div className="text-[11.5px] uppercase tracking-[0.14em] font-medium px-4">
          {t('polaroid.empty')}
        </div>
        {isFull && (
          <div className="text-[11px] font-num px-6" style={{ color: LT.green50 }}>
            {t('polaroid.emptyHint')}
          </div>
        )}
      </div>
    </div>
  </div>
);

function formatDate(iso, intlLocale) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(intlLocale || 'fr-BE', { day: 'numeric', month: 'short', year: 'numeric' });
}
